import { useEffect, useRef, useState } from "react";
import { X, Loader2, ShieldCheck } from "lucide-react";

const RESEND_SECONDS = 30;

const maskPhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    return digits.length > 4 ? `${"•".repeat(digits.length - 4)}${digits.slice(-4)}` : digits;
};

/**
 * OTP step shown before paying at a store that has OTPAllowed = 1.
 * Sends the OTP to the customer's own number as soon as it opens, then hands the entered OTP back.
 *
 * open, onClose
 * phone: customer's mobile number the OTP goes to
 * storeName: shown in the sub-heading
 * length: number of digits (default 6)
 * onSendOtp(phone): async, sends / re-sends the OTP; throw an Error to show its message
 * onSubmit(otp): async, called with the OTP string; throw an Error to show its message (e.g. a wrong OTP if you verify here)
 */
export default function OtpVerifyModal({ open, onClose, phone, storeName, length = 6, onSendOtp, onSubmit }) {
    const [digits, setDigits] = useState(() => Array(length).fill(""));
    const [sending, setSending] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [seconds, setSeconds] = useState(0);
    const inputs = useRef([]);
    const sentOnce = useRef(false);

    const otp = digits.join("");
    const complete = otp.length === length;

    const send = async () => {
        if (!phone) {
            setError("There is no mobile number on your account to send the OTP to.");
            return;
        }
        setSending(true);
        setError("");
        try {
            await onSendOtp?.(phone);
            setSeconds(RESEND_SECONDS);
            inputs.current[0]?.focus();
        } catch (err) {
            setError(err?.data?.message || err?.message || "Could not send the OTP. Please try again.");
        } finally {
            setSending(false);
        }
    };

    // Send once per opening; reset everything when it closes
    useEffect(() => {
        if (!open) {
            sentOnce.current = false;
            setDigits(Array(length).fill(""));
            setError("");
            setSeconds(0);
            return;
        }
        if (sentOnce.current) return;
        sentOnce.current = true;
        send();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (seconds <= 0) return;
        const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [seconds]);

    useEffect(() => {
        if (!open) return;
        const handleKey = (e) => e.key === "Escape" && !submitting && onClose();
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open, onClose, submitting]);

    if (!open) return null;

    const setDigit = (i, v) => setDigits((prev) => prev.map((d, idx) => (idx === i ? v : d)));

    const handleChange = (i, e) => {
        const v = e.target.value.replace(/\D/g, "");
        if (!v) return setDigit(i, "");
        setDigit(i, v.slice(-1));
        if (i < length - 1) inputs.current[i + 1]?.focus();
    };

    const handleKeyDown = (i, e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) {
            setDigit(i - 1, "");
            inputs.current[i - 1]?.focus();
        }
        if (e.key === "Enter" && complete) handleSubmit();
    };

    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
        if (!pasted) return;
        e.preventDefault();
        setDigits(Array.from({ length }, (_, i) => pasted[i] || ""));
        inputs.current[Math.min(pasted.length, length - 1)]?.focus();
    };

    const handleSubmit = async () => {
        if (!complete || submitting) return;
        setSubmitting(true);
        setError("");
        try {
            await onSubmit?.(otp);
        } catch (err) {
            setError(err?.data?.message || err?.message || "That OTP could not be verified.");
            setDigits(Array(length).fill(""));
            inputs.current[0]?.focus();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" role="dialog" aria-modal="true" aria-label="Verify OTP">
            <div className="absolute inset-0 bg-black/50" onClick={() => !submitting && onClose()} />
            <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="absolute right-4 top-4 rounded-full p-1.5 text-gray-500 transition-colors hover:bg-gray-100"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>

                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0B67FC]/10 text-[#0B67FC]">
                    <ShieldCheck size={22} />
                </span>
                <h2 className="mt-4 text-lg font-bold text-gray-900">Verify your number</h2>
                <p className="mt-1 text-sm text-gray-600">
                    {sending
                        ? "Sending an OTP to "
                        : "Enter the OTP we sent to "}
                    <span className="font-semibold text-gray-900">{maskPhone(phone)}</span>
                    {storeName ? ` to pay at ${storeName}.` : "."}
                </p>

                <div className="mt-5 flex justify-between gap-2" onPaste={handlePaste}>
                    {digits.map((d, i) => (
                        <input
                            key={i}
                            ref={(el) => (inputs.current[i] = el)}
                            type="text"
                            inputMode="numeric"
                            autoComplete={i === 0 ? "one-time-code" : "off"}
                            maxLength={1}
                            value={d}
                            onChange={(e) => handleChange(i, e)}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onFocus={(e) => e.target.select()}
                            aria-label={`OTP digit ${i + 1}`}
                            className={`h-12 w-full min-w-0 rounded-xl border text-center text-xl font-bold text-gray-900 outline-none transition-colors focus:border-[#0B67FC] focus:ring-2 focus:ring-[#0B67FC]/30 ${
                                error ? "border-rose-300" : "border-gray-200"
                            }`}
                        />
                    ))}
                </div>

                {error && <p className="mt-3 text-sm font-medium text-rose-600">{error}</p>}

                <div className="mt-4 text-sm text-gray-600">
                    {seconds > 0 ? (
                        <span>Resend OTP in {seconds}s</span>
                    ) : (
                        <button
                            type="button"
                            onClick={send}
                            disabled={sending || submitting}
                            className="font-semibold text-[#0B67FC] hover:underline disabled:opacity-50"
                        >
                            Resend OTP
                        </button>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!complete || submitting}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B67FC] py-3.5 text-base font-bold text-white shadow-sm transition-all hover:bg-[#0954D0] active:scale-[0.99] disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    Verify &amp; continue
                </button>
            </div>
        </div>
    );
}